import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { ukDay, ukTime } from "@/lib/time";
import { PageHead } from "@/components/hub/Shell";
import { Panel, Chips, Empty, Tb, Th, Td, CellMain, CellSub, St, Tag, openRow } from "@/components/portal/crm/kit";
import { RestoreDialog } from "@/components/hub/RestoreLane";

const gbp = (v) => `£${Math.round(Number(v || 0)).toLocaleString("en-GB")}`;
const RANGES = [{ key: "1", label: "Today" }, { key: "7", label: "Last 7 days" }, { key: "30", label: "Last 30 days" }, { key: "90", label: "90 days" }];
const KINDS = [{ key: "withdrawn", label: "Taken off by us" }, { key: "declined", label: "Passed by the desk" }, { key: "all", label: "Both" }];
const key = (x) => `${x.lead_id}:${x.lender_id}`;

/** Put cases back — every lane we took off a desk (or a desk passed on), ready to re-open in one go. */
export default function HubWithdrawn() {
  const [days, setDays] = useState("1");
  const [kind, setKind] = useState("withdrawn");
  const [desk, setDesk] = useState("");
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/hub/lanes/closed", { params: { days, kind, ...(desk ? { lender_id: desk } : {}) } });
      setData(r.data); setPicked([]);
    } catch { setData({ items: [], desks: [] }); toast.error("Couldn't load the closed lanes"); }
  }, [days, kind, desk]);
  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => data?.items || [], [data]);
  const all = items.length > 0 && picked.length === items.length;
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const chosen = items.filter((x) => picked.includes(key(x)));

  return (
    <div data-testid="hub-withdrawn">
      <PageHead eyebrow="Desk tools" title="Put cases back" testId="hub-withdrawn-head"
        sub="Every case taken off a desk — by you or by the SLA auto-pull — and every case a desk passed on. Pick the ones a desk has asked for back and re-open them in one go." />
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
        <Chips testPrefix="hub-withdrawn-days" value={days} onChange={setDays} items={RANGES} />
        <Chips testPrefix="hub-withdrawn-kind" value={kind} onChange={setKind} items={KINDS} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select className="inp" value={desk} onChange={(e) => setDesk(e.target.value)} style={{ minWidth: 260, width: "auto", maxWidth: 420 }} data-testid="hub-withdrawn-desk">
            <option value="">Every desk</option>
            {(data?.desks || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <button className="btn btn-primary" disabled={!picked.length} onClick={() => setOpen(true)} data-testid="hub-withdrawn-restore-selected">
            <Undo2 /> {picked.length ? `Put back ${picked.length} selected` : "Put back selected"}
          </button>
        </div>
      </div>
      {!data ? <Empty testId="hub-withdrawn-loading"><Loader2 className="animate-spin" /> Loading…</Empty> : items.length === 0 ? (
        <Panel testId="hub-withdrawn-empty"><Empty>Nothing closed in this window. Try a longer range or “Both”.</Empty></Panel>
      ) : (
        <Panel pad={false} testId="hub-withdrawn-list" title={`${items.length} closed lane${items.length === 1 ? "" : "s"}`}
               sub="Putting a case back re-opens it where it was, returns the paperwork to their portal and starts the clock fresh.">
          <Tb head={<><Th style={{ width: 44 }}><input type="checkbox" checked={all} onChange={() => setPicked(all ? [] : items.map(key))} data-testid="hub-withdrawn-select-all" /></Th>
            <Th>Case</Th><Th>Desk</Th><Th>Why it closed</Th><Th>When</Th><Th className="r">Now</Th></>}>
            {items.map((x) => <Line key={key(x)} x={x} on={picked.includes(key(x))} onToggle={() => toggle(key(x))} onDone={load} />)}
          </Tb>
        </Panel>
      )}
      <RestoreDialog open={open} onOpenChange={setOpen} items={chosen} onDone={load} testId="hub-withdrawn-dialog" />
    </div>
  );
}

function Line({ x, on, onToggle, onDone }) {
  const [open, setOpen] = useState(false);
  const id = key(x);
  const nav = useNavigate();
  return (
    <tr style={{ cursor: "pointer" }} onClick={openRow(nav, `/hub/leads/${x.lead_id}`)} data-testid={`hub-withdrawn-row-${id}`}>
      <Td><input type="checkbox" checked={on} onChange={onToggle} data-testid={`hub-withdrawn-pick-${id}`} /></Td>
      <Td className="lead">
        <CellMain><Link to={`/hub/leads/${x.lead_id}`} style={{ color: "inherit" }}>{x.case}</Link></CellMain>
        <CellSub>{x.reference} · {gbp(x.amount_gbp)} · {x.finance_type || "Business finance"}{x.introducer ? ` · ${x.introducer}` : ""}</CellSub>
      </Td>
      <Td><CellMain>{x.desk}</CellMain></Td>
      <Td>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
          <St tone={x.kind === "withdrawn" ? "amber" : "slate"}>{x.kind === "withdrawn" ? "Taken off" : "Passed"}</St>
          {x.auto && <Tag amber>SLA auto-pull</Tag>}
        </div>
        <CellSub style={{ margin: 0 }}>{x.reason || "No reason recorded"}</CellSub>
      </Td>
      <Td><div className="date">{ukDay(x.at)} · {ukTime(x.at)}</div></Td>
      <Td className="r">
        <CellSub style={{ margin: "0 0 6px" }}>{x.live_lanes ? `${x.live_lanes} desk${x.live_lanes === 1 ? "" : "s"} still on it` : "With no desk"}</CellSub>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(true)} data-testid={`hub-withdrawn-restore-${id}`}>Put back</button>
        <RestoreDialog open={open} onOpenChange={setOpen} items={[x]} onDone={onDone} testId={`hub-withdrawn-dialog-${id}`} />
      </Td>
    </tr>
  );
}
